import { useState } from 'react';

import type { InventoryEntryType } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync } from '@solar/ui';

import { api } from '../api';

/**
 * 재고 등록 — 레거시 inventory/edit(빈 폼 진입) 파리티.
 *
 * 단말을 골라 입고/출고 갯수를 수기 등록한다. 등록 행의 회사 귀속과
 * 등록자 기록(입고만 users_id 저장)은 서버가 처리한다.
 */

export function InventoryEdit() {
  // 단말 선택지 — 단말 마스터 전체(서버 size 상한 200. 초과분은 목록 화면에서 처리).
  const terminals = useAsync(() => api.listTerminals({ size: 200 }), []);

  const [terminalId, setTerminalId] = useState('');
  const [entryType, setEntryType] = useState<InventoryEntryType>('add');
  const [count, setCount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function submit() {
    const n = Number(count);
    if (terminalId === '') {
      setError('단말을 선택해 주세요.');
      return;
    }
    if (!Number.isInteger(n) || n < 1) {
      setError('갯수는 1 이상의 정수여야 합니다.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.createInventoryEntry({
        def_terminal_id: Number(terminalId),
        entry_type: entryType,
        count: n,
      });
      setCount('');
      setOk('등록했습니다. 재고 목록에서 합계를 확인할 수 있습니다.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin__body admin__body--single">
      <section className="admin__list">
        <div className="detail__head">
          <h2>재고 등록</h2>
        </div>

        {terminals.loading && <Spinner />}
        {terminals.error && <ErrorBox message={terminals.error} onRetry={terminals.reload} />}

        {terminals.data && (
          <section className="panel form-grid create-form">
            <label className="f wide">
              단말 *
              <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)}>
                <option value="">선택</option>
                {terminals.data.items.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.model ? ` (${t.model})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="f wide">
              구분 *
              <select
                value={entryType}
                onChange={(e) => setEntryType(e.target.value as InventoryEntryType)}
              >
                <option value="add">입고</option>
                <option value="minus">출고</option>
              </select>
            </label>
            <label className="f wide">
              갯수 *
              <input
                type="number"
                min={1}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                placeholder="1 이상의 정수"
              />
            </label>

            {error && <p className="error">{error}</p>}
            {ok && <p className="ok">{ok}</p>}

            <div className="row form-actions">
              <button
                type="button"
                className="primary"
                disabled={busy}
                onClick={() => void submit()}
              >
                {busy ? '등록 중…' : '등록'}
              </button>
            </div>
          </section>
        )}
      </section>
    </div>
  );
}
